/**
 * EditActionsDropdown.tsx - Fundraiser Management Actions Dropdown
 *
 * This component provides a dropdown menu with various management actions
 * for fundraisers including editing, sharing, copying, and deletion.
 *
 * Key Features:
 * - Comprehensive action menu for fundraiser management
 * - Click-outside detection for dropdown closing
 * - Icon-based actions with clear labeling
 * - Conditional action availability based on fundraiser state
 * - Responsive dropdown positioning
 * - Keyboard navigation support
 *
 * Available Actions:
 * - Edit: Open fundraiser in builder
 * - Donate: Navigate to fundraiser page
 * - Invite: Open promoter invitation
 * - Link: Manage fundraiser links
 * - Share: Social sharing options
 * - Embed: Generate embed codes
 * - Copy: Duplicate fundraiser
 * - Status: Update fundraiser status
 * - Delete: Remove fundraiser with confirmation
 *
 * Props:
 * - onEdit: () => void - Edit action handler
 * - onDonate: () => void - Donate action handler
 * - onInvite: () => void - Invite action handler
 * - onLink: () => void - Link management handler
 * - onShare: () => void - Share action handler
 * - onEmbed: () => void - Embed action handler
 * - onCopy: () => void - Copy action handler
 * - onStatus: () => void - Status update handler
 * - onDelete: () => void - Delete action handler
 *
 * Dependencies: Styled-components, Lucide icons, click-outside detection
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  ChevronDown,
  Link2,
  Share2,
  Copy,
  Trash2,
  ToggleLeft,
  Code,
  Pen,
  Heart,
  UserPlus,
} from "lucide-react";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButtonContainer = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f1f5f9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EditMainButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  border-radius: 12px 0 0 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  background: transparent;
  border: none;
  border-left: 1px solid #e2e8f0;
  color: #1e293b;
  cursor: pointer;
  border-radius: 0 12px 12px 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  color: #1e293b;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen ? "translateY(4px)" : "translateY(-4px)"};
  transition: all 0.2s ease;
  margin-top: 4px;
`;

const DropdownItem = styled.button<{ variant?: "danger" }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#1e293b")};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${(props) =>
      props.variant === "danger" ? "#fef2f2" : "#f8fafc"};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ItemIcon = styled.div<{ variant?: "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#64748b")};
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
`;

interface EditActionsDropdownProps {
  onEdit: () => void;
  onInvite?: () => void;
  onUpdateLink: () => void;
  onShare: () => void;
  onCopy?: () => void;
  onEmbed: () => void;
  onStatus?: () => void;
  onDelete?: () => void;
  hideWhenNoId?: boolean;
}

export const EditActionsDropdown: React.FC<EditActionsDropdownProps> = ({
  onEdit,
  onInvite,
  onUpdateLink,
  onShare,
  onCopy,
  onEmbed,
  onStatus,
  onDelete,
  hideWhenNoId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleEditClick = () => {
    onEdit();
  };
  const handleOptionClick = (onClick?: () => void) => {
    if (onClick) {
      onClick();
    }
    setIsOpen(false);
  };

  const hasAnyActions =
    (!hideWhenNoId && (onUpdateLink || onShare || onEmbed)) ||
    onInvite ||
    onStatus ||
    onCopy ||
    onDelete;

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButtonContainer>
        <EditMainButton onClick={handleEditClick} type="button">
          <Pen size={16} />
          Edit
        </EditMainButton>
        <DropdownTrigger
          type="button"
          onClick={hasAnyActions ? handleDropdownToggle : undefined}
          disabled={!hasAnyActions}
          style={{
            opacity: hasAnyActions ? 1 : 0.4,
            cursor: hasAnyActions ? "pointer" : "not-allowed",
          }}
        >
          <ChevronIcon size={16} isOpen={isOpen} />
        </DropdownTrigger>
      </DropdownButtonContainer>

      <DropdownMenu isOpen={isOpen}>
        {!hideWhenNoId && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onUpdateLink)}
          >
            <ItemIcon>
              <Link2 size={16} />
            </ItemIcon>
            Link
          </DropdownItem>
        )}

        {!hideWhenNoId && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onShare)}
          >
            <ItemIcon>
              <Share2 size={16} />
            </ItemIcon>
            Share
          </DropdownItem>
        )}
        {!hideWhenNoId && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onEmbed)}
          >
            <ItemIcon>
              <Code size={16} />
            </ItemIcon>
            Embed
          </DropdownItem>
        )}

        {onInvite && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onInvite)}
          >
            <ItemIcon>
              <UserPlus size={16} />
            </ItemIcon>
            Invite
          </DropdownItem>
        )}

        {onStatus && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onStatus)}
          >
            <ItemIcon>
              <ToggleLeft size={16} />
            </ItemIcon>
            Status
          </DropdownItem>
        )}
        {onCopy && (
          <DropdownItem type="button" onClick={() => handleOptionClick(onCopy)}>
            <ItemIcon>
              <Copy size={16} />
            </ItemIcon>
            Copy
          </DropdownItem>
        )}
        {onDelete && (
          <>
            <Divider />
            <DropdownItem
              variant="danger"
              type="button"
              onClick={() => handleOptionClick(onDelete)}
            >
              <ItemIcon variant="danger">
                <Trash2 size={16} />
              </ItemIcon>
              Delete
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};
